import {
  buildFrostField,
  FROST_BASE_FRONT,
  type FrostField,
  type FrostHole,
  fillFrostMask,
  groupByWidth,
  hasFrostArea,
  holeGradient,
  visibleCracks,
} from "./frost";

/**
 * Отрисовка льда в DOM: маска матового слоя и канвас трещин. Расчёт — в
 * `frost.ts`, цикл кадров — в `frost-controller.ts`; здесь только то, что
 * требует браузера (канвас, `getComputedStyle`), поэтому юнит-тестов нет:
 * вид проверяется глазами (tasks.md 4.1).
 */

/** Плотность пикселей канваса трещин: фиксированная, как в макете. */
const CRACK_DPR = 2;

export interface FrostPainterElements {
  /** Корень оверлея: от него читаются токены цвета. */
  root: HTMLElement;
  /** Матовый слой с `backdrop-filter`; прячется, когда льда нет. */
  frost: HTMLElement;
  /** Канвас трещин. */
  lines: HTMLCanvasElement;
}

export interface FrostPainter {
  /** Размер экрана изменился: пересобрать сетку и трещины (тот же seed). */
  resize(width: number, height: number): void;
  /** Перечитать токены цвета трещин: зовётся при каждом переходе заморозки. */
  refreshColors(): void;
  /** Текущий размер, px. */
  size(): { width: number; height: number };
  /** Нарисовать лёд при данном положении фронта. */
  draw(front: number): void;
  /** Вырезать круги оттаивания (§118) в матовом слое и в трещинах, не трогая маску льда. */
  drawHoles(holes: readonly FrostHole[], radius: number): void;
}

interface CrackColors {
  body: string;
  shadow: string;
  highlight: string;
}

function readColors(root: HTMLElement): CrackColors {
  const style = getComputedStyle(root);
  return {
    body: style.getPropertyValue("--frost-crack").trim(),
    shadow: style.getPropertyValue("--frost-crack-shadow").trim(),
    highlight: style.getPropertyValue("--frost-crack-highlight").trim(),
  };
}

export function createFrostPainter(elements: FrostPainterElements): FrostPainter {
  const { root, frost, lines } = elements;
  const maskCanvas = document.createElement("canvas");
  const offscreen = document.createElement("canvas");
  let field: FrostField = buildFrostField(0, 0);
  let image: ImageData | null = null;
  let colors = readColors(root);
  /** Маска льда матового слоя (`url(...)`) и градиенты кругов: слои маски собираются из них. */
  let iceMask = "";
  let holeLayers: string[] = [];

  /**
   * Слои маски складываются пересечением (`intersect` / `source-in`): лёд видно
   * там, где его пропускает и маска льда, и каждый круг. Маска льда и трещины
   * ради круга не пересчитываются.
   */
  const applyMasks = (): void => {
    const intersect = (element: HTMLElement, layers: string[]): void => {
      const value = layers.length > 0 ? layers.join(", ") : "none";
      element.style.maskImage = value;
      element.style.webkitMaskImage = value;
      element.style.maskSize = "100% 100%";
      element.style.webkitMaskSize = "100% 100%";
      element.style.maskComposite = "intersect";
      element.style.webkitMaskComposite = "source-in";
    };
    intersect(frost, iceMask === "" ? [] : [iceMask, ...holeLayers]);
    intersect(lines, holeLayers);
  };

  const clearLines = (): void => {
    const context = lines.getContext("2d");
    if (context !== null) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, lines.width, lines.height);
    }
  };

  return {
    resize(width, height) {
      colors = readColors(root);
      if (!hasFrostArea(width, height)) {
        // Нулевая сторона: сетки, маски и канвасов нет, все три в одном состоянии.
        field = buildFrostField(0, 0);
        image = null;
        maskCanvas.width = maskCanvas.height = 0;
        lines.width = lines.height = offscreen.width = offscreen.height = 0;
        frost.style.display = "none";
        return;
      }
      field = buildFrostField(width, height);
      maskCanvas.width = field.cols;
      maskCanvas.height = field.rows;
      image = maskCanvas.getContext("2d")?.createImageData(field.cols, field.rows) ?? null;
      lines.width = offscreen.width = width * CRACK_DPR;
      lines.height = offscreen.height = height * CRACK_DPR;
    },

    refreshColors() {
      colors = readColors(root);
    },

    size: () => ({ width: field.width, height: field.height }),

    drawHoles(holes, radius) {
      holeLayers = holes.map((hole) => holeGradient(hole, radius));
      applyMasks();
    },

    draw(front) {
      const maskContext = maskCanvas.getContext("2d");
      if (front <= FROST_BASE_FRONT || image === null || maskContext === null) {
        // Льда нет или маски ещё нет: слой с размытием не показываем.
        frost.style.display = "none";
        clearLines();
        return;
      }
      frost.style.display = "";

      fillFrostMask(image.data, field.eff, front);
      maskContext.putImageData(image, 0, 0);
      iceMask = `url(${maskCanvas.toDataURL()})`;
      applyMasks();

      const context = lines.getContext("2d");
      const layerContext = offscreen.getContext("2d");
      if (context === null || layerContext === null) {
        return;
      }
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, lines.width, lines.height);
      const groups = groupByWidth(visibleCracks(field.cracks, front));
      const paths = new Map<number, Path2D>();
      for (const [width, cracks] of groups) {
        const path = new Path2D();
        for (const crack of cracks) {
          path.moveTo(crack.x1, crack.y1);
          path.lineTo(crack.x2, crack.y2);
        }
        paths.set(width, path);
      }

      // Каждый слой рисуется непрозрачным вне экрана и переносится с прозрачностью:
      // полупрозрачные круглые концы на стыках дали бы светлые точки.
      const layer = (
        color: string,
        alpha: number,
        shift: number,
        widthFactor: number,
        widthAdd: number,
        minWidth: number,
      ): void => {
        layerContext.setTransform(1, 0, 0, 1, 0, 0);
        layerContext.clearRect(0, 0, offscreen.width, offscreen.height);
        layerContext.setTransform(CRACK_DPR, 0, 0, CRACK_DPR, shift * CRACK_DPR, shift * CRACK_DPR);
        layerContext.lineCap = "round";
        layerContext.lineJoin = "round";
        layerContext.strokeStyle = color;
        for (const [width, path] of paths) {
          if (width < minWidth) {
            continue;
          }
          layerContext.lineWidth = width * widthFactor + widthAdd;
          layerContext.stroke(path);
        }
        context.globalAlpha = alpha;
        context.drawImage(offscreen, 0, 0);
        context.globalAlpha = 1;
      };
      layer(colors.shadow, 0.15, 1.2, 1, 1, 0);
      layer(colors.body, 0.3, 0, 1, 0, 0);
      layer(colors.highlight, 0.22, 0, 0.3, 0, 1.5);
    },
  };
}
